import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Col,
  Container,
  Row,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Button,
} from "reactstrap";
import Breadcrumbs from "components/Common/Breadcrumb";
import { useVehicleStock } from "hooks/stockHook";
import classnames from "classnames";
import ImageGallery from "./ImageGallery";
import QuickActions from "./QuickActions";
import Pricing from "./Pricing";
import AdditionalInformation from "./AdditionalInformation";
import DetailsTab from "./DetailsTab";
import MoneyReceiptsTab from "./MoneyReceiptsTab";
import DeliveryChallansTab from "./DeliveryChallansTab";
import QuotationsTab from "./QuotationsTab";
import VehicleInfo from "./VehicleInfo";
import DocumentGenerationModal from "./DocumentGenerationModal";
import { PLACEHOLDERS } from "assets/images";
import ShareVehicleImages from "components/Common/ShareVehicleImages";
import ImageZipDownloader from "./ImageZipDownloader";
import StatusBadge from "components/Common/StatusBadge";
import LiveIndicator from "components/Common/LiveIndicator/LiveIndicator";
import InventoryTab from "./InventoryTab";
import ViewOnWebsite from "./ViewOnWebsite";

const StockView = () => {
  const { stockId } = useParams()
  const navigate = useNavigate()
  const { data, isLoading } = useVehicleStock(stockId)
  const stock = data?.data || {}
  const [activeTab, setActiveTab] = useState("details")
  const [docType, setDocType] = useState("")
  const [moneyReceiptPage, setMoneyReceiptPage] = useState(1)
  const [deliveryChallanPage, setDeliveryChallanPage] = useState(1)
  const [quotationPage, setQuotationPage] = useState(1)
  const [inventoryPage, setInventoryPage] = useState(1)

  document.title = `Vehicle Stock${
    stock?.brand
      ? ` | ${stock?.brand.name} ${stock?.model?.name} ${stock?.stock_id}`
      : ""
  }`

  const toggleTab = tab => {
    if (activeTab !== tab) setActiveTab(tab)
  }

  const carImages = stock?.images?.length
    ? stock?.images?.map(item => item?.url)
    : [PLACEHOLDERS.comingSoon]

  const addNewStock = () => {
    navigate(`/stock-add-edit`)
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs
          title="Vehicle Stock"
          breadcrumbItem={"Details"}
          loading={isLoading}
        />
        {/* Header */}
        <Row className="mb-4">
          <Col lg="12">
            <VehicleInfo stock={stock} loading={isLoading} />
            <div className="badge badge-soft-primary">
              <i
                className="fas fa-map-marker-alt me-1"
                style={{ fontSize: 7 }}
              ></i>
              {stock?.showroom?.name ?? ""} -{" "}
              {stock?.showroom?.short_name ?? ""}
            </div>
            {stock?.is_published_on_website && (
              <div className="mt-1">
                <LiveIndicator text="Live on Website" />
              </div>
            )}

            <div className="text-muted d-block mt-1 text-wrap">
              <StatusBadge
                trueLabel="Published"
                falseLabel="Draft"
                trueColor="primary"
                falseColor="warning"
                value={stock?.is_published}
              />
            </div>
            <div className="mt-2">
              <ViewOnWebsite stock_id={stock?.stock_id} />
            </div>
          </Col>
          <Col sm="12" className="text-end">
            <Button color="primary" onClick={addNewStock}>
              Add New Stock
            </Button>
          </Col>
        </Row>

        <Row>
          {/* Left Column */}
          <Col lg="4" className="mb-4">
            <ImageGallery
              stock={stock}
              carImages={carImages}
              loading={isLoading}
            />
            <ShareVehicleImages vehicleId={stock?.id} />

            <ImageZipDownloader vehicleId={stock?.stock_id} />
            <QuickActions setDocType={setDocType} />
            <Pricing stock={stock} loading={isLoading} />
            <AdditionalInformation stock={stock} loading={isLoading} />
          </Col>

          {/* Right Column */}
          <Col lg="8">
            <Nav pills className="nav-pills-custom mb-4">
              {[
                {
                  id: "details",
                  label: "Vehicle Details",
                  icon: "bx bx-detail",
                },
                {
                  id: "money-receipts",
                  label: "Money Receipts",
                  icon: "bx bx-receipt",
                },
                {
                  id: "delivery-challans",
                  label: "Delivery Challans",
                  icon: "bx bx-package",
                },
                {
                  id: "quotations",
                  label: "Quotations",
                  icon: "bx bx-file",
                },
                {
                  id: "inventory",
                  label: "Inventory",
                  icon: "bx bx-note",
                },
              ]?.map(tab => (
                <NavItem key={tab.id}>
                  <NavLink
                    className={classnames({ active: activeTab === tab.id })}
                    onClick={() => toggleTab(tab.id)}
                  >
                    {tab.icon && <i className={`${tab.icon} me-2`}></i>}
                    {tab.label}
                    {tab.count !== undefined && ` (${tab.count})`}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>

            <TabContent activeTab={activeTab}>
              {/* Details Tab */}
              <TabPane tabId="details">
                <DetailsTab stock={stock} loading={isLoading} />
              </TabPane>

              {/* Money Receipts Tab */}
              <TabPane tabId="money-receipts">
                <MoneyReceiptsTab setMoneyReceiptPage={setMoneyReceiptPage} />
              </TabPane>

              {/* Delivery Challans Tab */}
              <TabPane tabId="delivery-challans">
                <DeliveryChallansTab
                  setDeliveryChallanPage={setDeliveryChallanPage}
                />
              </TabPane>

              {/* Quotations Tab */}
              <TabPane tabId="quotations">
                <QuotationsTab setQuotationPage={setQuotationPage} />
              </TabPane>
              {/* Inventory Tab */}
              <TabPane tabId="inventory">
                <InventoryTab setInventoryPage={setInventoryPage} />
              </TabPane>
            </TabContent>
          </Col>
        </Row>
      </Container>

      {/* Document Generation Modal */}
      <DocumentGenerationModal
        docType={docType}
        setDocType={setDocType}
        stock={stock}
      />
    </div>
  )
}

export default StockView
